/**
 * TopicsToWriteTab — dedicated "Create Content · Topics to write on" view.
 *
 * Aggregates two topic sources already generated for the audit:
 *   1. `data.trending_topics` — voice/industry-tuned topic clusters
 *      produced by the content-prep pipeline.
 *   2. `data.research_bundle.topics` — research-backed topic candidates
 *      (fallback / secondary list when trending_topics is empty).
 *
 * Each row shows the topic label + a "Draft prompt post" quick action
 * that deep-links into Create Content · Prompt Posts pre-populated with
 * the topic.
 */
import React from 'react';
import { PenLine, Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './../ui/card';
import { Button } from './../ui/button';

export default function TopicsToWriteTab({ data, onDraftFromTopic }) {
  const trending = Array.isArray(data?.trending_topics) ? data.trending_topics : [];
  const research = (data?.research_bundle?.topics || []).filter((t) => t);
  const prepStatus = data?.content_prep_status || 'pending';

  const hasAny = trending.length > 0 || research.length > 0;

  return (
    <div className="space-y-6" data-testid="topics-to-write-tab">
      <div>
        <p className="font-mono-edge text-[10px] tracking-[0.18em] text-primary">
          CREATE CONTENT · TOPICS TO WRITE ON
        </p>
        <h2 className="text-2xl font-semibold mt-1">
          Topics your audience is already engaging with
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Curated from what your peers, competitors, and industry
          thought leaders are posting about right now — filtered to
          topics that match your voice and objective.
        </p>
      </div>

      {!hasAny && prepStatus !== 'ready' && (
        <Card className="border-dashed border-border" data-testid="topics-empty-preparing">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
              <Sparkles size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold">Preparing your topics…</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Content prep runs asynchronously after each audit. Come
                back in a minute or open Prompt Posts to trigger a
                targeted generation right away.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!hasAny && prepStatus === 'ready' && (
        <Card className="border-dashed border-border" data-testid="topics-empty-ready">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold">No topics surfaced yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              We couldn&apos;t confidently extract topic clusters for
              this profile. Head into Prompt Posts and describe the
              angle you want — the LLM will still generate posts on
              demand.
            </p>
          </CardContent>
        </Card>
      )}

      {trending.length > 0 && (
        <TopicList
          heading="Trending in your voice & objective"
          items={trending}
          onDraftFromTopic={onDraftFromTopic}
          testId="topics-trending-list"
        />
      )}
      {research.length > 0 && (
        <TopicList
          heading="From your research bundle"
          items={research}
          onDraftFromTopic={onDraftFromTopic}
          testId="topics-research-list"
        />
      )}
    </div>
  );
}

function TopicList({ heading, items, onDraftFromTopic, testId }) {
  return (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-5">
        <h3 className="text-sm font-mono-edge tracking-[0.14em] uppercase text-muted-foreground mb-3">
          {heading}
        </h3>
        <ul className="divide-y divide-border">
          {items.map((t, idx) => {
            const label = typeof t === 'string' ? t : (t?.topic || t?.title || t?.label || '');
            const why = typeof t === 'object' ? (t?.why || t?.reason || '') : '';
            if (!label) return null;
            return (
              <li
                key={`${label}-${idx}`}
                className="py-3 flex items-start justify-between gap-4 group"
                data-testid={`topic-row-${idx}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug">{label}</p>
                  {why ? (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{why}</p>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0 opacity-70 group-hover:opacity-100"
                  onClick={() => onDraftFromTopic?.(label)}
                  data-testid={`topic-draft-btn-${idx}`}
                >
                  <PenLine size={13} className="mr-1.5" />
                  Draft a post
                  <ArrowRight size={12} className="ml-1 opacity-70" />
                </Button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
